/**
 * My profile — all roles can view and edit their own profile.
 * Designation / sales role / office changes require admin approval (non-admin staff).
 */
import { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../store/auth';
import ProfileEditor from '../components/ProfileEditor';

const STATUS_CHIP = {
    active: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-amber-100 text-amber-800',
    suspended: 'bg-rose-100 text-rose-800',
    rejected: 'bg-rose-100 text-rose-800',
};

export default function Profile() {
    const loadMe = useAuth(s => s.loadMe);
    const [user, setUser] = useState(null);
    const [pending, setPending] = useState(null);
    const [directory, setDirectory] = useState({ designations: [], offices: [] });
    const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
    const [pwdBusy, setPwdBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get('/users/me/profile');
        setUser(data.user);
        setPending(data.pendingDesignationChange);
    };

    useEffect(() => {
        load();
        api.get('/org-directory')
            .then(r => setDirectory({
                designations: (r.data.grouped?.designation || []).filter(d => d.active),
                offices: (r.data.grouped?.office || []).filter(o => o.active),
            }))
            .catch(() => {});
    }, []);

    const changePassword = async (e) => {
        e.preventDefault();
        if (pwd.next.length < 8) return toast.error('New password must be at least 8 characters');
        if (pwd.next !== pwd.confirm) return toast.error('Passwords do not match');
        setPwdBusy(true);
        try {
            await api.post('/auth/change-password', {
                currentPassword: pwd.current,
                newPassword: pwd.next,
            });
            toast.success('Password updated');
            setPwd({ current: '', next: '', confirm: '' });
        } catch {
            /* toasted */
        } finally {
            setPwdBusy(false);
        }
    };

    if (!user) {
        return <div className="ag-card p-10 text-center text-sm text-ink-500">Loading…</div>;
    }

    return (
        <div className="space-y-4 max-w-4xl">
            <div>
                <h1 className="text-2xl font-extrabold">My profile</h1>
                <p className="text-sm text-ink-500">Update your details. Job designation changes need admin approval.</p>
            </div>

            <div className="ag-card p-5 flex gap-4 items-start">
                <div className="h-14 w-14 rounded-full bg-brand-teal-50 text-brand-teal-700 grid place-items-center text-lg font-extrabold uppercase shrink-0">
                    {user.name?.split(/\s+/).map(w => w[0]).slice(0, 2).join('') || '?'}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-bold">{user.name}</h2>
                        <span className={`ag-chip ${STATUS_CHIP[user.status] || 'bg-ink-100'}`}>{user.status}</span>
                        <span className="ag-chip bg-ink-100 text-ink-700">{user.role}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-ink-600">
                        <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {user.email}</span>
                        {user.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {user.phone}</span>}
                        {(user.officeBranch || user.territory) && (
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {user.officeBranch || user.territory}</span>
                        )}
                    </div>
                </div>
            </div>

            <ProfileEditor
                user={user}
                directory={directory}
                mode="self"
                pendingDesignationChange={pending}
                onSaved={async (data) => {
                    if (data?.user) setUser(data.user);
                    if (data?.pendingDesignationChange !== undefined) {
                        setPending(data.pendingDesignationChange);
                    } else {
                        await load();
                    }
                    await loadMe();
                }}
            />

            <div className="ag-card p-5 space-y-3">
                <h3 className="font-bold text-sm uppercase tracking-wide text-ink-500 inline-flex items-center gap-2">
                    <Lock className="h-4 w-4" /> Change password
                </h3>
                <form className="grid grid-cols-1 sm:grid-cols-3 gap-3" onSubmit={changePassword}>
                    <div>
                        <label className="ag-label">Current password</label>
                        <input type="password" className="ag-input" required value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} />
                    </div>
                    <div>
                        <label className="ag-label">New password</label>
                        <input type="password" className="ag-input" required minLength={8} value={pwd.next} onChange={e => setPwd({ ...pwd, next: e.target.value })} />
                    </div>
                    <div>
                        <label className="ag-label">Confirm new password</label>
                        <input type="password" className="ag-input" required value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} />
                    </div>
                    <div className="sm:col-span-3 flex justify-end">
                        <button type="submit" className="ag-btn-primary" disabled={pwdBusy}>
                            {pwdBusy ? 'Updating…' : 'Update password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
